import { api } from './api';
import type {
  SourcePreview,
  Pipeline, PipelineArtifact, PipelineRun, PipelineSource,
  PaginatedPipelineResponse, PaginatedPipelineRunResponse, PaginatedPipelineSourceResponse,
} from '../types';

interface ApiResponse<T> { success: boolean; code: number; message: string; data: T }

export interface PipelineListParams {
  page?: number; page_size?: number; search?: string; status?: string;
  include_deleted?: boolean; sort_by?: string; sort_order?: 'asc' | 'desc'; filters?: string; app_id?: string;
  include_global?: boolean;
}

export interface PipelineUpsertRequest {
  /** The knowledge base this recipe indexes. Null keeps the pipeline owning its own
   *  sources, which is every pipeline written before knowledge bases existed. */
  kbRef?: string | null;
  appId?: string;
  pipelineCode?: string;
  name?: string;
  description?: string | null;
  supportedSourceTypes?: string[];
  parserConfig?: Record<string, boolean>;
  cleaningConfig?: Record<string, boolean>;
  chunkingConfig?: {
    strategy: string; chunkSize: number; chunkOverlap: number;
    /** Named defaults for a document type — DEFAULT, LEGAL, HOSPITAL. */
    preset?: string | null;
    /** PAGE keeps a chunk inside one page; DOCUMENT lets a section survive a page break. */
    scope?: string | null;
    /** Regex marking a structural boundary; its first capture group names the section. */
    boundaryPattern?: string | null;
  };
  metadataConfig?: Record<string, boolean>;
  embeddingConfig?: { modelRef: string; dimensions?: number | null };
  outputConfig?: { type: string; provider: string; connectionId?: string | null; indexName?: string | null };
  status?: string;
}

export const pipelinesService = {
  // ---- recipes ----
  list: async (params?: PipelineListParams): Promise<PaginatedPipelineResponse> =>
    (await api.get<PaginatedPipelineResponse>('/api/v1/pipelines/list', { params })).data,
  getById: async (id: string): Promise<Pipeline> =>
    (await api.get<ApiResponse<Pipeline>>(`/api/v1/pipelines/get/${id}`)).data.data,
  create: async (data: PipelineUpsertRequest): Promise<Pipeline> =>
    (await api.post<ApiResponse<Pipeline>>('/api/v1/pipelines/create', data)).data.data,
  update: async (id: string, data: PipelineUpsertRequest): Promise<Pipeline> =>
    (await api.put<ApiResponse<Pipeline>>(`/api/v1/pipelines/update/${id}`, data)).data.data,
  softDelete: async (id: string) =>
    (await api.delete<ApiResponse<{ _id: string }>>(`/api/v1/pipelines/delete/${id}`)).data.data,
  restore: async (id: string): Promise<Pipeline> =>
    (await api.post<ApiResponse<Pipeline>>(`/api/v1/pipelines/restore/${id}`)).data.data,

  // ---- sources ----
  listSources: async (pipelineId: string, params?: { status?: string; search?: string; page?: number; page_size?: number }) =>
    (await api.get<PaginatedPipelineSourceResponse>(`/api/v1/pipelines/${pipelineId}/sources`, { params })).data,
  uploadSource: async (pipelineId: string, file: File, meta?: { title?: string; description?: string }): Promise<PipelineSource> => {
    const form = new FormData();
    form.append('file', file);
    if (meta?.title) form.append('title', meta.title);
    if (meta?.description) form.append('description', meta.description);
    // The shared instance defaults to application/json, which would override the
    // multipart boundary; set per request as every other upload here does.
    const res = await api.post<ApiResponse<PipelineSource>>(`/api/v1/pipelines/${pipelineId}/sources/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data;
  },
  addSource: async (pipelineId: string, data: { sourceType: 'TEXT' | 'URL'; text?: string; url?: string; title?: string; description?: string }): Promise<PipelineSource> =>
    (await api.post<ApiResponse<PipelineSource>>(`/api/v1/pipelines/${pipelineId}/sources`, data)).data.data,
  removeSource: async (pipelineId: string, sourceId: string) =>
    (await api.delete<ApiResponse<{ _id: string }>>(`/api/v1/pipelines/${pipelineId}/sources/${sourceId}`)).data.data,
  /** One source in full — metadata, provenance, images and text. The list omits
   *  these: across a 15,000-document corpus they are megabytes nobody reads. */
  getSource: async (pipelineId: string, sourceId: string): Promise<PipelineSource> =>
    (await api.get<ApiResponse<PipelineSource>>(`/api/v1/pipelines/${pipelineId}/sources/${sourceId}`)).data.data,
  /** The stored file as a Blob. Fetched with the session's token, which is why the
   *  viewer cannot point an <iframe> at the URL directly. */
  getSourceFile: async (pipelineId: string, sourceId: string): Promise<Blob> =>
    (await api.get(`/api/v1/pipelines/${pipelineId}/sources/${sourceId}/file`, { responseType: 'blob' })).data,
  getSourcePreview: async (pipelineId: string, sourceId: string): Promise<SourcePreview> =>
    (await api.get<ApiResponse<SourcePreview>>(`/api/v1/pipelines/${pipelineId}/sources/${sourceId}/preview`)).data.data,
  validateSource: async (pipelineId: string, sourceId: string): Promise<PipelineSource> =>
    (await api.post<ApiResponse<PipelineSource>>(`/api/v1/pipelines/${pipelineId}/sources/${sourceId}/validate`)).data.data,

  // ---- runs ----
  run: async (pipelineId: string, sourceIds?: string[]): Promise<PipelineRun> =>
    (await api.post<ApiResponse<PipelineRun>>(`/api/v1/pipelines/${pipelineId}/run`, sourceIds?.length ? { sourceIds } : {})).data.data,
  listRuns: async (params?: { pipeline_id?: string; status?: string; page?: number; page_size?: number }) =>
    (await api.get<PaginatedPipelineRunResponse>('/api/v1/pipeline-runs', { params })).data,
  getRun: async (runRef: string): Promise<PipelineRun> =>
    (await api.get<ApiResponse<PipelineRun>>(`/api/v1/pipeline-runs/${runRef}`)).data.data,
  cancelRun: async (runRef: string): Promise<PipelineRun> =>
    (await api.post<ApiResponse<PipelineRun>>(`/api/v1/pipeline-runs/${runRef}/cancel`)).data.data,

  // ---- artifacts ----
  listArtifacts: async (pipelineId: string, status?: string): Promise<PipelineArtifact[]> =>
    (await api.get<ApiResponse<PipelineArtifact[]>>(`/api/v1/pipelines/${pipelineId}/artifacts`, { params: status ? { status } : undefined })).data.data,
  getArtifact: async (id: string): Promise<PipelineArtifact> =>
    (await api.get<ApiResponse<PipelineArtifact>>(`/api/v1/pipeline-artifacts/${id}`)).data.data,
  retireArtifact: async (id: string): Promise<PipelineArtifact> =>
    (await api.post<ApiResponse<PipelineArtifact>>(`/api/v1/pipeline-artifacts/${id}/retire`)).data.data,
};
